import { cloneElement, ReactElement, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ListboxButton } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ExclamationCircleIcon, ExclamationTriangleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';

import { cn } from 'support/helpers/cn/cn';
import { FieldState } from 'support/types/ui';

import { dropdownClasses } from '../styles';
import { DropdownSize } from '../types';

const buttonBorderClassByState: Record<FieldState, string> = {
  default: 'border-gray-300 active:border-procuros-green-500 focus-visible:border-procuros-green-500',
  error: 'border-red-500',
  warning: 'border-warning-500',
  'pending-validation': 'border-blue-500',
};

const buttonRingClassByState: Record<FieldState, (open: boolean) => string> = {
  default: (open) => (open ? 'border-procuros-green-500' : ''),
  error: (open) =>
    open ? 'ring-2 ring-red-500' : 'focus-visible:ring-2 focus-visible:ring-red-500 focus:ring-2 focus:ring-red-500',
  warning: (open) =>
    open
      ? 'ring-2 ring-warning-500'
      : 'focus-visible:ring-2 focus-visible:ring-warning-500 focus:ring-2 focus:ring-warning-500',
  'pending-validation': (open) =>
    open ? 'ring-2 ring-blue-500' : 'focus-visible:ring-2 focus-visible:ring-blue-500 focus:ring-2 focus:ring-blue-500',
};

type DropdownButtonProps = {
  selectedOptions: Array<{ label: string }>;
  emptySelectionLabel?: string;
  showSelection?: boolean;
  size: DropdownSize;
  buttonClassName?: string;
  fieldState?: FieldState;
  open: boolean;
  leftIcon?: ReactElement<{ className?: string }>;
  rightIcon?: ReactElement<{ className?: string }>;
  disabled?: boolean;
  name?: string;
  referenceProps: any;
  onBlur: React.FocusEventHandler<HTMLButtonElement>;
  onFocus?: () => void;
  displayTestId?: string;
};

export function DropdownButton({
  selectedOptions,
  emptySelectionLabel,
  showSelection = true,
  size = 'base',
  buttonClassName,
  fieldState = 'default',
  open,
  leftIcon,
  rightIcon,
  name,
  referenceProps,
  onBlur,
  onFocus,
  displayTestId,
}: DropdownButtonProps) {
  const { t } = useTranslation();

  const trailingIconByState: Record<FieldState, ReactNode> = {
    default: null,
    error: (
      <ExclamationCircleIcon
        className={cn(dropdownClasses.rightIcon.size[size], 'bg-white text-red-500')}
        aria-hidden="true"
      />
    ),
    warning: (
      <ExclamationTriangleIcon
        className={cn(dropdownClasses.rightIcon.size[size], 'bg-white text-warning-500')}
        aria-hidden="true"
      />
    ),
    'pending-validation': (
      <MinusCircleIcon
        className={cn(dropdownClasses.rightIcon.size[size], 'bg-white text-blue-500')}
        aria-hidden="true"
      />
    ),
  };

  // Determine what text to display in the button
  const buttonText =
    !showSelection && emptySelectionLabel
      ? emptySelectionLabel || t('components.select.pleaseChoose')
      : selectedOptions.length
        ? selectedOptions.map((option) => t(option.label)).join(', ')
        : emptySelectionLabel || t('components.select.pleaseChoose');

  return (
    <ListboxButton
      className={cn(
        buttonBorderClassByState[fieldState],
        buttonRingClassByState[fieldState](open),
        { 'text-gray-500': !selectedOptions.length },
        'relative flex min-w-0 flex-1 cursor-default items-center overflow-hidden border bg-white text-left shadow-xs focus-visible:ring-0 focus-visible:outline-hidden active:ring-0 active:outline-hidden disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-400',
        dropdownClasses.button.size[size],
        buttonClassName ?? 'rounded-md',
      )}
      data-name={name}
      data-field={name}
      {...referenceProps}
      onBlur={onBlur}
      onFocus={onFocus}
    >
      {leftIcon &&
        cloneElement(leftIcon, {
          ...leftIcon.props,
          className: cn(dropdownClasses.leftIcon.size[size], leftIcon.props.className),
        })}
      <span className="block truncate" data-testid={displayTestId}>
        {buttonText}
      </span>

      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
        {rightIcon &&
          cloneElement(rightIcon, {
            ...rightIcon.props,
            className: cn(dropdownClasses.rightIcon.size[size], rightIcon.props.className),
          })}
        {trailingIconByState[fieldState]}
        <ChevronDownIcon className="size-5 text-gray-400" aria-hidden="true" />
      </span>
    </ListboxButton>
  );
}
